import React, {
	useMemo,
	memo,
	useRef,
	cloneElement,
	useEffect,
	forwardRef,
	useImperativeHandle,
} from 'react';
import Frame from 'react-frame-component';
import { WithStyleWrapper } from './WithStyleWrapper';
import styles from './index.module.scss';
import { getIframeWidth } from './utils';
import { useThrottledElementSize } from '../../hooks/useThrottledElementSize';

type IframePropsType = {
	children: React.ReactElement;
	onIframeResize: ({
		width,
		height,
	}: {
		width: number;
		height: number;
	}) => void;
	style?: {
		width?: string;
		height?: string;
		pointerEvents?: string;
	} & React.CSSProperties;
};

export interface Handler {
	triggerIframeRecalculation(): void;
}

export const IframeComponent = forwardRef<Handler, IframePropsType>(
	({ children, onIframeResize, style }, ref) => {
		const [squareRef, { width: elWidth, height: elHeight }] =
			useThrottledElementSize();
		const iframeRef = useRef<HTMLIFrameElement>(null);
		const { width, height, pointerEvents, ...restStyle } = {
			width: '300',
			...style,
		};

		// NOTE: This event dispatch is required to re-trigger the recalculation in the useElementSize hook.
		function triggerIframeRecalculation() {
			window.dispatchEvent(new Event('resize'));
		}

		useImperativeHandle(ref, () => ({ triggerIframeRecalculation }), []);

		useEffect(() => {
			if (!elWidth || !elHeight) return;
			onIframeResize({ width: elWidth, height: elHeight });
		}, [elWidth, elHeight]);

		const child = useMemo(
			() =>
				cloneElement(children, {
					style: restStyle,
					onImgLoad: triggerIframeRecalculation,
				}),
			[children]
		);

		return (
			<Frame
				className={styles.iframe}
				style={{
					width: getIframeWidth(width, elWidth),
					pointerEvents,
				}}
				ref={iframeRef}
				frameBorder="0"
				scrolling="no"
				height={elHeight || 'auto'}
				initialContent={`<!DOCTYPE html><html><head></head><body class=${styles.iframeBody}><div id="mountHere"></div></body></html>`}
				mountTarget="#mountHere"
			>
				<WithStyleWrapper>
					<section className={styles.sectionWrapper} ref={squareRef}>
						{child}
					</section>
				</WithStyleWrapper>
			</Frame>
		);
	}
);

export const Iframe = memo(IframeComponent);
